import {type SwitchProps} from '@radix-ui/react-switch'
import {useId} from 'react'
import {tv, type VariantProps} from 'tailwind-variants'
import Toggle from '~/design-system/foundations/Toggle'

type HgToggleProps = {
  label?: React.ReactNode
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
} & Omit<SwitchProps, 'id' | 'className'> &
  VariantProps<typeof hgToggleVariants>

const hgToggleVariants = tv({
  slots: {
    root: 'peer flex h-20 w-[36px] items-center rounded-4xl border border-border-input px-1 outline-offset-4 outline-border-primary focus-visible:outline focus-visible:outline-2 data-[state=checked]:border-border-primary data-[state=checked]:bg-surface-primary data-[state=unchecked]:bg-surface-frosted focus-visible:data-[state=checked]:bg-surface-primary-hover focus-visible:data-[state=unchecked]:bg-surface-frosted-hover group-hover/toggle:data-[state=checked]:bg-surface-primary-hover group-hover/toggle:data-[state=unchecked]:bg-surface-frosted-hover',
    thumb:
      'flex h-16 w-16 rounded-full bg-surface-elevated shadow-[0px_10px_16px_0px_rgba(28,28,35,0.02),0px_6px_10px_0px_rgba(28,28,35,0.04),0px_0px_3px_0px_rgba(28,28,35,0.09)]',
    label:
      'cursor-pointer pl-s2 text-text-subdued arcadia-ui-1 group-hover/toggle:text-text-default peer-focus-visible:text-text-default',
  },
  variants: {
    hovered: {
      true: {
        root: 'data-[state=checked]:bg-surface-primary-hover data-[state=unchecked]:bg-surface-frosted-hover',
        thumb: 'bg-surface-elevated-hover',
        label: 'text-text-default',
      },
    },
    focused: {
      true: {
        root: 'outline outline-2 data-[state=checked]:bg-surface-primary-hover data-[state=unchecked]:bg-surface-frosted-hover',
        thumb: 'bg-surface-elevated-hover',
        label: 'text-text-default',
      },
    },
    checked: {
      true: {
        root: 'bg-surface-primary',
      },
    },
  },
})

export default function HgToggle({
  label,
  checked,
  onCheckedChange,
  hovered,
  focused,
  ...props
}: HgToggleProps) {
  const id = useId()
  const {
    root,
    thumb,
    label: labelClassName,
  } = hgToggleVariants({
    hovered,
    focused,
    checked,
  })
  return (
    <div className="group/toggle flex items-center justify-center">
      <Toggle
        {...props}
        id={id}
        classNames={{
          root: root(),
          thumb: thumb(),
        }}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      {label && (
        <label className={labelClassName()} htmlFor={id}>
          {label}
        </label>
      )}
    </div>
  )
}
